import React, { useState, useRef, useEffect } from "react";
import Draggable from "react-draggable";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import { FiMinus, FiSquare, FiCopy, FiX } from "react-icons/fi";

const COLOR = "#bb0000";

const SFWindow = ({
    title = "Window",
    children,
    onClose,
    defaultWidth = 800,
    defaultHeight = 600,
    minWidth = 400,
    minHeight = 300,
    showMinimizeIcon,
    showMaximizeIcon,
}) => {
    const nodeRef = useRef(null);
    const [minimized, setMinimized] = useState(false);
    const [maximized, setMaximized] = useState(false);
    const [position, setPosition] = useState({
        x: (window.innerWidth - defaultWidth) / 2,
        y: (window.innerHeight - defaultHeight) / 2
    });

    useEffect(() => {
        console.debug("position>>??", position)

        const windowWidth = nodeRef.current?.offsetWidth || defaultWidth;
        const windowHeight = nodeRef.current?.offsetHeight || defaultHeight;

        let newX = position.x;
        let newY = position.y;

        // Left
        if (position.x < 0) {
            newX = 0;
        }
        // Top
        if (position.y < 0) {
            newY = 0;
        }
        // Right
        if (position.x + windowWidth >= window.innerWidth) {
            newX = window.innerWidth - windowWidth;
        }
        // Bottom
        if (position.y + windowHeight >= window.innerHeight) {
            newY = window.innerHeight - windowHeight;
        }

        if (newX !== position.x || newY !== position.y) {
            setPosition({ x: newX, y: newY });
        }
    }, [position]);

    return (
        <>
            {/* Minimized Floating Button */}
            {minimized && (
                <Box sx={{ position: 'fixed', bottom: 20, right: 20, zIndex: 999999 }}>
                    <Tooltip title={`Restore ${title}`}>
                        <IconButton
                            onClick={() => setMinimized(false)}
                            sx={{
                                backgroundColor: COLOR,
                                color: 'white',
                                width: 56,
                                height: 56,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                '&:hover': { backgroundColor: '#e40606' },
                            }}
                        >
                            <FiSquare size={24} />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}

            {/* Backdrop for Maximized */}
            {maximized && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        zIndex: 99,
                        width: '100%',
                        height: "100%",
                        background: "black",
                        opacity: 0.5
                    }}
                />
            )}

            {/* Main Window */}
            <Draggable
                nodeRef={nodeRef}
                handle=".window-header"
                position={maximized ? { x: 0, y: 0 } : position}
                onDrag={(e, d) => !maximized && setPosition({ x: d.x, y: d.y })}
                disabled={maximized}
            >
                <Box
                    ref={nodeRef}
                    sx={{
                        position: "fixed",
                        top: maximized ? "2vh" : 0,
                        left: maximized ? "2vw" : 0,
                        width: maximized ? "96vw" : defaultWidth,
                        height: maximized ? "96vh" : defaultHeight,
                        zIndex: 999999,
                        background: "white",
                        boxShadow: "0 10px 40px rgba(0,0,0,0.35)",
                        borderRadius: 2,
                        display: minimized ? "none" : "flex",
                        flexDirection: "column",
                        resize: maximized ? "none" : "both",
                        overflow: "hidden",
                        minWidth: minWidth,
                        minHeight: minHeight,
                    }}
                >
                    {/* HEADER */}
                    <Box
                        className="window-header"
                        sx={{
                            background: COLOR,
                            color: "white",
                            p: 1,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            cursor: "move",
                        }}
                    >
                        <Typography fontWeight={600} color="#fff">
                            {title}
                        </Typography>

                        <Box display="flex" gap="6px">
                            {showMinimizeIcon ?
                                <IconButton size="small" onClick={() => setMinimized(true)}>
                                    <FiMinus color="white" />
                                </IconButton>
                                : null}

                            {showMaximizeIcon ?
                                <IconButton size="small" onClick={() => setMaximized(!maximized)}>
                                    {maximized ? (
                                        <FiCopy style={{ transform: "scaleX(-1)" }} color="white" />
                                    ) : (
                                        <FiSquare color="white" />
                                    )}
                                </IconButton>
                                : null}

                            <IconButton size="small" onClick={onClose}>
                                <FiX color="white" />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* BODY */}
                    <Box
                        sx={{
                            flex: 1,
                            overflow: "auto",
                            backgroundColor: "#fff",
                            padding: '1rem'
                        }}
                    >
                        {children}
                    </Box>
                </Box>
            </Draggable>
        </>
    );
};

export default SFWindow;